import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Download, Users, ArrowRight, X } from 'lucide-react';
import { useApp } from '../context/AppContext';

function Import() {
  const { parseCSVForDuplicates, importCSV } = useApp();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Duplicate handling state
  const [duplicateData, setDuplicateData] = useState(null);
  const [duplicateAction, setDuplicateAction] = useState('skip'); // 'skip' | 'merge' | 'create' | 'review'
  const [individualActions, setIndividualActions] = useState({});

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
        setResult(null);
        setDuplicateData(null);
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
      setDuplicateData(null);
    }
  };

  const handleCheckDuplicates = async () => {
    if (!file) return;

    setParsing(true);
    try {
      const data = await parseCSVForDuplicates(file);
      setDuplicateData(data);

      // Initialize individual actions for review mode
      const actions = {};
      data.duplicates.forEach(dup => {
        actions[dup.csvLine] = 'skip';
      });
      setIndividualActions(actions);
    } catch (error) {
      setResult({ error: error.message || 'Failed to parse CSV' });
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    try {
      let actionToUse = duplicateAction;

      // If review mode, use individual actions
      if (duplicateAction === 'review') {
        actionToUse = individualActions;
      }

      const res = await importCSV(file, actionToUse);
      setResult(res);
      setFile(null);
      setDuplicateData(null);
    } catch (error) {
      setResult({ error: error.message || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setDuplicateData(null);
    setResult(null);
    setDuplicateAction('skip');
    setIndividualActions({});
  };

  const updateIndividualAction = (csvLine, action) => {
    setIndividualActions(prev => ({
      ...prev,
      [csvLine]: action
    }));
  };

  const setAllIndividualActions = (action) => {
    const actions = {};
    duplicateData.duplicates.forEach(dup => {
      actions[dup.csvLine] = action;
    });
    setIndividualActions(actions);
  };

  const sampleCSV = `First Name,Last Name,Email,Phone,Company,Job Title,LinkedIn URL,LinkedIn Connected Date,Category,How We Met,Notes
John,Doe,john@example.com,555-0100,Acme Corp,Developer,https://linkedin.com/in/johndoe,2024-01-15,professional,Tech conference,Great to work with
Jane,Smith,jane@example.com,555-0101,StartupXYZ,CEO,https://linkedin.com/in/janesmith,2023-06-20,professional,LinkedIn,
Bob,Wilson,bob@example.com,555-0102,,,,,friends,College,`;

  const downloadSample = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_contacts.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>
          Import Contacts
        </h1>
        <p className="text-sm text-muted">
          Import contacts from CSV files or LinkedIn exports
        </p>
      </div>

      {/* Duplicate Review Modal */}
      {duplicateData && duplicateData.duplicates.length > 0 && !result && (
        <div className="card" style={{ marginBottom: '1.5rem', border: '2px solid var(--warning)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div>
              <h2 className="card-title" style={{ color: 'var(--warning)', marginBottom: '0.5rem' }}>
                <Users size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Duplicates Found
              </h2>
              <p className="text-sm text-muted">
                Found {duplicateData.duplicates.length} potential duplicate{duplicateData.duplicates.length !== 1 ? 's' : ''} and {duplicateData.newContacts.length} new contact{duplicateData.newContacts.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button className="btn btn-ghost" onClick={handleCancel}>
              <X size={20} />
            </button>
          </div>

          {/* Action Selection */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">How should duplicates be handled?</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {[
                { value: 'skip', label: 'Skip All', desc: 'Ignore duplicates, only import new contacts' },
                { value: 'merge', label: 'Merge All', desc: 'Update existing contacts with new data' },
                { value: 'create', label: 'Create All', desc: 'Import duplicates as new contacts' },
                { value: 'review', label: 'Review Each', desc: 'Decide for each duplicate individually' }
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDuplicateAction(option.value)}
                  style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '0.5rem',
                    border: '2px solid',
                    borderColor: duplicateAction === option.value ? 'var(--primary)' : 'var(--gray-200)',
                    background: duplicateAction === option.value ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    flex: '1',
                    minWidth: '140px'
                  }}
                >
                  <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>{option.label}</div>
                  <div className="text-xs text-muted">{option.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Individual Review */}
          {duplicateAction === 'review' && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span className="text-sm font-medium">Review each duplicate:</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAllIndividualActions('skip')}>
                    Skip All
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAllIndividualActions('merge')}>
                    Merge All
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAllIndividualActions('create')}>
                    Create All
                  </button>
                </div>
              </div>

              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: '0.5rem' }}>
                {duplicateData.duplicates.map((dup, index) => (
                  <div
                    key={dup.csvLine}
                    style={{
                      padding: '0.75rem 1rem',
                      borderBottom: index < duplicateData.duplicates.length - 1 ? '1px solid var(--gray-100)' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="font-medium">
                          {dup.csvData.first_name} {dup.csvData.last_name}
                        </span>
                        <ArrowRight size={14} className="text-muted" />
                        <span className="text-muted">
                          {dup.existingContact.first_name} {dup.existingContact.last_name}
                        </span>
                      </div>
                      <div className="text-xs text-muted">
                        Match by {dup.matchType}: {dup.matchType === 'email' ? dup.csvData.email : 'same name'}
                      </div>
                    </div>
                    <select
                      value={individualActions[dup.csvLine] || 'skip'}
                      onChange={(e) => updateIndividualAction(dup.csvLine, e.target.value)}
                      className="form-input form-select"
                      style={{ width: 'auto', padding: '0.375rem 0.75rem' }}
                    >
                      <option value="skip">Skip</option>
                      <option value="merge">Merge</option>
                      <option value="create">Create New</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          <div style={{
            padding: '0.75rem 1rem',
            background: 'var(--gray-50)',
            borderRadius: '0.5rem',
            marginBottom: '1rem'
          }}>
            <div className="text-sm">
              <strong>Summary:</strong>{' '}
              {duplicateData.newContacts.length} new contacts will be imported
              {duplicateAction === 'skip' && `, ${duplicateData.duplicates.length} duplicates will be skipped`}
              {duplicateAction === 'merge' && `, ${duplicateData.duplicates.length} existing contacts will be updated`}
              {duplicateAction === 'create' && `, ${duplicateData.duplicates.length} duplicates will be created as new`}
              {duplicateAction === 'review' && (
                <>
                  , {Object.values(individualActions).filter(a => a === 'skip').length} skipped
                  , {Object.values(individualActions).filter(a => a === 'merge').length} merged
                  , {Object.values(individualActions).filter(a => a === 'create').length} created as new
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleImport} disabled={importing}>
              {importing ? 'Importing...' : 'Proceed with Import'}
            </button>
          </div>
        </div>
      )}

      {/* No duplicates - direct import */}
      {duplicateData && duplicateData.duplicates.length === 0 && !result && (
        <div className="card" style={{ marginBottom: '1.5rem', border: '2px solid var(--success)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--success)', marginBottom: '1rem' }}>
            <CheckCircle size={24} />
            <div>
              <div className="font-medium">No Duplicates Found</div>
              <div className="text-sm text-muted">
                Ready to import {duplicateData.newContacts.length} new contact{duplicateData.newContacts.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleImport} disabled={importing}>
              {importing ? 'Importing...' : 'Import Contacts'}
            </button>
          </div>
        </div>
      )}

      <div className="import-layout">
        {/* Upload Section */}
        <div className="import-upload">
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '1rem' }}>
              Upload CSV File
            </h2>

            {/* Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              style={{
                border: `2px dashed ${dragActive ? 'var(--primary)' : 'var(--gray-300)'}`,
                borderRadius: '0.75rem',
                padding: '3rem 2rem',
                textAlign: 'center',
                background: dragActive ? 'rgba(99, 102, 241, 0.05)' : 'var(--gray-50)',
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
              onClick={() => document.getElementById('file-input').click()}
            >
              <Upload
                size={48}
                style={{
                  color: dragActive ? 'var(--primary)' : 'var(--gray-400)',
                  marginBottom: '1rem'
                }}
              />
              <p style={{ marginBottom: '0.5rem' }}>
                {dragActive
                  ? 'Drop your file here'
                  : 'Drag and drop your CSV file here'}
              </p>
              <p className="text-sm text-muted">
                or click to browse
              </p>
              <input
                id="file-input"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>

            {/* Selected File */}
            {file && !duplicateData && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: 'var(--gray-50)',
                borderRadius: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <FileText size={24} className="text-muted" />
                <div style={{ flex: 1 }}>
                  <div className="font-medium">{file.name}</div>
                  <div className="text-sm text-muted">
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleCheckDuplicates}
                  disabled={parsing}
                >
                  {parsing ? 'Checking...' : 'Check & Import'}
                </button>
              </div>
            )}

            {/* Result */}
            {result && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                borderRadius: '0.5rem',
                background: result.error
                  ? 'rgba(239, 68, 68, 0.1)'
                  : 'rgba(34, 197, 94, 0.1)'
              }}>
                {result.error ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--danger)' }}>
                    <AlertCircle size={24} />
                    <div>
                      <div className="font-medium">Import Failed</div>
                      <div className="text-sm">{result.error}</div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--success)', marginBottom: '0.5rem' }}>
                      <CheckCircle size={24} />
                      <div className="font-medium">Import Successful</div>
                    </div>
                    <div className="text-sm">
                      {result.imported > 0 && <div>Imported {result.imported} new contact{result.imported !== 1 ? 's' : ''}</div>}
                      {result.merged > 0 && <div>Merged {result.merged} existing contact{result.merged !== 1 ? 's' : ''}</div>}
                      {result.skipped > 0 && <div>Skipped {result.skipped} duplicate{result.skipped !== 1 ? 's' : ''}</div>}
                      {result.errors > 0 && <div style={{ color: 'var(--warning)' }}>{result.errors} error{result.errors !== 1 ? 's' : ''}</div>}
                    </div>
                    {result.errorDetails && result.errorDetails.length > 0 && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--warning)' }}>
                        {result.errorDetails.slice(0, 3).map((err, i) => (
                          <div key={i}>Line {err.line}: {err.error}</div>
                        ))}
                        {result.errorDetails.length > 3 && (
                          <div>...and {result.errorDetails.length - 3} more errors</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="import-instructions">
          <div className="card">
            <h2 className="card-title" style={{ marginBottom: '1rem' }}>
              CSV Format
            </h2>
            <p className="text-sm" style={{ marginBottom: '1rem' }}>
              Your CSV file should have a header row with column names. The following columns are supported:
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 className="text-sm font-semibold" style={{ marginBottom: '0.5rem' }}>Required</h3>
              <ul className="text-sm" style={{ paddingLeft: '1.5rem', color: 'var(--gray-600)' }}>
                <li>First Name (or FirstName, First)</li>
              </ul>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 className="text-sm font-semibold" style={{ marginBottom: '0.5rem' }}>Optional</h3>
              <ul className="text-sm" style={{ paddingLeft: '1.5rem', color: 'var(--gray-600)' }}>
                <li>Last Name</li>
                <li>Email (or Email Address)</li>
                <li>Phone (or Phone Number, Mobile)</li>
                <li>Company (or Organization)</li>
                <li>Job Title (or Title, Position)</li>
                <li>LinkedIn URL (or LinkedIn)</li>
                <li>LinkedIn Connected Date (or Connected On)</li>
                <li>Twitter (or Twitter Handle)</li>
                <li>Address</li>
                <li>Birthday</li>
                <li>How We Met</li>
                <li>Interests</li>
                <li>Category</li>
                <li>Notes</li>
              </ul>
            </div>

            <button
              className="btn btn-secondary"
              onClick={downloadSample}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              <Download size={18} />
              Download Sample CSV
            </button>
          </div>

          <div className="card mt-4">
            <h2 className="card-title" style={{ marginBottom: '1rem' }}>
              LinkedIn Export
            </h2>
            <p className="text-sm text-muted" style={{ marginBottom: '1rem' }}>
              You can export your LinkedIn connections and import them here:
            </p>
            <ol className="text-sm" style={{ paddingLeft: '1.5rem', color: 'var(--gray-600)' }}>
              <li style={{ marginBottom: '0.5rem' }}>Go to LinkedIn Settings & Privacy</li>
              <li style={{ marginBottom: '0.5rem' }}>Select "Get a copy of your data"</li>
              <li style={{ marginBottom: '0.5rem' }}>Choose "Connections"</li>
              <li style={{ marginBottom: '0.5rem' }}>Download and extract the ZIP file</li>
              <li>Upload the Connections.csv file here</li>
            </ol>
          </div>
        </div>
      </div>

      <style>{`
        .import-layout {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 2rem;
        }
        @media (max-width: 768px) {
          .import-layout {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          .import-upload {
            order: 1;
          }
          .import-instructions {
            order: 2;
          }
        }
        ul, ol {
          margin: 0;
        }
        li {
          margin-bottom: 0.25rem;
        }
      `}</style>
    </div>
  );
}

export default Import;
